import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/auth.service';
import logo from '../assets/EidosGo.png';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login, applyAuthResponse } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const response = await login(email, password);
      if (response.mfaRequired && response.mfaToken) {
        localStorage.removeItem('mfaRememberToken');
        setMfaRequired(true);
        setMfaToken(response.mfaToken);
        return;
      }
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const response = await authService.verifyMfaCode(
        mfaToken,
        mfaCode,
        rememberDevice
      );
      if (response.rememberToken) {
        localStorage.setItem('mfaRememberToken', response.rememberToken);
      }
      applyAuthResponse(response);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || 'MFA verification failed');
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = '/api/auth/google';
  };

  const handleMicrosoftLogin = () => {
    window.location.href = '/api/auth/microsoft';
  };

  return (
    <div style={containerStyle}>
      <div style={formBoxStyle}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
          <img
            src={logo}
            alt="EidosGo"
            style={{ width: '180px', height: 'auto' }}
          />
        </div>

        {!mfaRequired ? (
          <form onSubmit={handleSubmit} style={formStyle}>
            <div style={inputGroupStyle}>
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={inputStyle}
              />
            </div>

            <div style={inputGroupStyle}>
              <label>Password</label>
              <div style={passwordContainerStyle}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={eyeButtonStyle}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && <div style={errorStyle}>{error}</div>}

            <button type="submit" style={buttonStyle}>Login</button>

          </form>
        ) : (
          <form onSubmit={handleMfaSubmit} style={formStyle}>
            <div style={inputGroupStyle}>
              <label>Verification code</label>
              <input
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                required
                maxLength={6}
                inputMode="numeric"
                style={inputStyle}
              />
              <span style={helperTextStyle}>
                Enter the 6-digit code sent to your email.
              </span>
            </div>

            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
              />
              Remember this browser for 30 days
            </label>

            {error && <div style={errorStyle}>{error}</div>}

            <button type="submit" style={buttonStyle}>Verify</button>
          </form>
        )}

        {!mfaRequired && (
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            style={linkButtonStyle}
          >
            Forgot password?
          </button>
        )}

        {!mfaRequired && (
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <p style={{ marginBottom: '1rem' }}>Or sign in with:</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button onClick={handleGoogleLogin} style={oauthButtonStyle}>Google</button>
              <button onClick={handleMicrosoftLogin} style={oauthButtonStyle}>Microsoft</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: '#ecf0f1'
};

const formBoxStyle: React.CSSProperties = {
  background: 'white',
  padding: '2rem',
  borderRadius: '8px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  width: '400px',
  maxWidth: '90%'
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
};

const inputGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
};

const passwordContainerStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center'
};

const inputStyle: React.CSSProperties = {
  padding: '0.75rem',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '1rem',
  width: '100%'
};

const eyeButtonStyle: React.CSSProperties = {
  position: 'absolute',
  right: '0.75rem',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '0.25rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#666',
  transition: 'color 0.2s'
};

const buttonStyle: React.CSSProperties = {
  padding: '0.75rem',
  background: '#3498db',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  fontSize: '1rem',
  cursor: 'pointer'
};

const oauthButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  background: '#2c3e50',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer'
};

const linkButtonStyle: React.CSSProperties = {
  marginTop: '1.5rem',
  background: 'none',
  border: 'none',
  color: '#2c3e50',
  cursor: 'pointer',
  textDecoration: 'underline'
};

const errorStyle: React.CSSProperties = {
  color: '#e74c3c',
  fontSize: '0.9rem',
  padding: '0.5rem',
  background: '#ffe6e6',
  borderRadius: '4px'
};

const helperTextStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  color: '#666'
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.95rem',
  color: '#2c3e50'
};

export default Login;
